import React, { useState } from "react";
import { Pagination } from "@design-system/Pagination/Pagination";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import { VStack } from "@design-system/VStack/VStack";
import {
    BooleanControl,
    NumberControl,
    VariantGrid,
    VariantItem,
} from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import gridStyles from "./playgroundControls.module.scss";

const sizes = ["md", "lg"] as const;
const buttonPositions = ["left", "right"] as const;

type PaginationSize = (typeof sizes)[number];
type ButtonPosition = (typeof buttonPositions)[number];

const clampPage = (page: number, pageCount: number) =>
    Math.min(Math.max(page, 1), pageCount);

export const PaginationPlayground = () => {
    const [currentPage, setCurrentPage] = useState(3);
    const [pageCount, setPageCount] = useState(12);
    const [size, setSize] = useState<PaginationSize>("lg");
    const [buttonsPosition, setButtonsPosition] =
        useState<ButtonPosition>("left");
    const [isDisabled, setIsDisabled] = useState(false);

    return (
        <SandboxLayout
            controls={
                <>
                    <section>
                        <h2>Pages</h2>
                        <VStack gap={16}>
                            <NumberControl
                                label="Current page"
                                value={currentPage}
                                min={1}
                                max={pageCount}
                                onChange={(value) =>
                                    setCurrentPage(clampPage(value, pageCount))
                                }
                            />
                            <NumberControl
                                label="Page count"
                                value={pageCount}
                                min={1}
                                max={999}
                                onChange={(value) => {
                                    const nextPageCount = clampPage(value, 999);

                                    setPageCount(nextPageCount);
                                    setCurrentPage((page) =>
                                        clampPage(page, nextPageCount),
                                    );
                                }}
                            />
                        </VStack>
                    </section>
                    <section>
                        <h2>Size</h2>
                        <ToggleGroup.Root<PaginationSize>
                            value={size}
                            onValueChange={(value) => {
                                if (value) {
                                    setSize(value);
                                }
                            }}
                        >
                            {sizes.map((option) => (
                                <ToggleGroup.Item key={option} value={option}>
                                    {option}
                                </ToggleGroup.Item>
                            ))}
                        </ToggleGroup.Root>
                    </section>
                    <section>
                        <h2>Button position</h2>
                        <ToggleGroup.Root<ButtonPosition>
                            value={buttonsPosition}
                            onValueChange={(value) => {
                                if (value) {
                                    setButtonsPosition(value);
                                }
                            }}
                        >
                            {buttonPositions.map((option) => (
                                <ToggleGroup.Item key={option} value={option}>
                                    {option}
                                </ToggleGroup.Item>
                            ))}
                        </ToggleGroup.Root>
                    </section>
                    <section>
                        <h2>State</h2>
                        <BooleanControl
                            label="Disabled"
                            checked={isDisabled}
                            onChange={setIsDisabled}
                        />
                    </section>
                </>
            }
        >
            <ShowcasePreview
                summary={`Page ${currentPage} of ${pageCount}`}
                width="wide"
                output={JSON.stringify(
                    {
                        currentPage,
                        pageCount,
                        size,
                        buttonsPosition,
                        disabled: isDisabled,
                    },
                    null,
                    2,
                )}
            >
                <div className={gridStyles.fillWidth}>
                    <VariantGrid title="Page navigation" columns="full">
                        <VariantItem label="Pagination">
                            <Pagination
                                currentPage={currentPage}
                                pageCount={pageCount}
                                onPageChange={setCurrentPage}
                                size={size}
                                buttonsPosition={buttonsPosition}
                                disabled={isDisabled}
                            />
                        </VariantItem>
                    </VariantGrid>
                </div>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
